import { useDeviceType } from '@cian/ui-kit';
import { Button } from '@cian/ui-kit/button';
import { IconList16 } from '@cian/ui-kit/icons';
import * as React from 'react';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as styles from './ListingModalContainer.css';
import { trackListingButtonClick } from '../../../../packages/Listing';
// eslint-disable-next-line import-x/no-restricted-paths
import { RedesignedFiltersPanelId } from '../../../FiltersMobile';
import { closeOfferPreview } from '../../../common/actions/closeOfferPreview/closeOfferPreview';
import { fetchHybridSearchData } from '../../../common/actions/fetchHybridSearchData';
import {
  closeMobileListing,
  EListingModalMode,
  EOffersFetchingMode,
  toggleModal,
} from '../../../common/actions/listingModal';
import { ETestId } from '../../../common/constants';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';
import { ChatModalContainer } from '../../../common/containers/ChatModalContainer';
import { PortalWrapper } from '../../../common/containers/PortalWrapper';
import { SortContainer } from '../../../common/containers/SortContainer';
import { SwipeModal, useMaxModalHeight } from '../../../common/containers/SwipeModal';
import { selectMlSearchSessionGuid } from '../../../common/selectors/analytics';
import { selectIsModalOpen } from '../../../common/selectors/listingModal';
import { selectIsListingPending, selectListingSeoData } from '../../../common/selectors/listingOffers';
import { selectMaxOffersCount } from '../../../common/selectors/listingOffers/selectMaxOffersCount';
import { selectQueryStringWithQMark } from '../../../common/selectors/queryString';
import { selectResultsJsonQuery } from '../../../common/selectors/results';
import { IListingOffersState } from '../../../common/types/listingOffers';
import { IApplicationState, TThunkDispatch } from '../../../common/types/redux';
import { ModalHeader } from '../../components';
import { trackListingShow } from '../../tracking';
import { trackListingClose } from '../../tracking/trackListingClose';
import { trackListingSwipeClose } from '../../tracking/trackListingSwipeClose';
import { ListingMobileContainer } from '../ListingMobile';

export const ListingModalContainer = () => {
  const { config } = useApplicationContext();
  const dispatch = useDispatch<TThunkDispatch>();
  const deviceType = useDeviceType();
  const jsonQuery = useSelector(selectResultsJsonQuery);
  const show = useSelector(selectIsModalOpen);
  const modalMode = useSelector((state: IApplicationState) => state.listingModal.mode);
  const isHalfScreenMode = React.useMemo(() => modalMode === EListingModalMode.HALF_SCREEN, [modalMode]);
  const anchorRef = React.useRef<HTMLElement | null>(null);
  const anchorDomId = RedesignedFiltersPanelId;
  const seoData = useSelector(selectListingSeoData);
  const isSeoEnabled = config.get<boolean>('dailyrent.seo.Enabled');
  const seoTitle = isSeoEnabled ? seoData.h1 : null;
  const listingOffersState = useSelector<IApplicationState, IListingOffersState>(state => state.listingOffers);
  const hasListingOffers = Boolean(listingOffersState.offers?.length);
  const queryString = useSelector(selectQueryStringWithQMark);
  const offersCount = useSelector(selectMaxOffersCount);
  const isPending = useSelector(selectIsListingPending);
  const mlSearchSessionGuid = useSelector(selectMlSearchSessionGuid);

  const maxHeight = useMaxModalHeight(anchorRef);

  const shouldListenSwipeDown = React.useMemo(() => isHalfScreenMode, [isHalfScreenMode]);

  const onClose = useCallback(() => {
    dispatch(closeOfferPreview());
    dispatch(closeMobileListing());
  }, [dispatch]);

  const onCloseButtonClick = React.useCallback(() => {
    trackListingClose(jsonQuery, queryString);
    onClose();
  }, [jsonQuery, queryString, onClose]);

  const onSwipeClose = useCallback(() => {
    trackListingSwipeClose({ jsonQuery, listingType: 'listing' });
    onClose();
  }, [jsonQuery, onClose]);

  const onOpen = React.useCallback(() => {
    trackListingButtonClick(jsonQuery);

    dispatch(
      toggleModal({
        open: true,
        mode: EListingModalMode.FULL_SCREEN,
        offersFetchingMode: EOffersFetchingMode.ALL,
      }),
    );

    if (!hasListingOffers) {
      dispatch(
        fetchHybridSearchData({
          shouldLoadListingOverride: true,
          deviceType,
        }),
      ).then(response => {
        if (response?.listingOffers) {
          trackListingShow('all_objects_on_map')(response.listingOffers);
        }
      });
    } else {
      trackListingShow('all_objects_on_map')({
        offers: listingOffersState.offers,
        mlRanking: listingOffersState.mlRanking || undefined,
        jsonQuery,
        queryString,
        offersCount,
        mlSearchSessionGuid,
      });
    }
  }, [
    dispatch,
    jsonQuery,
    hasListingOffers,
    listingOffersState.offers,
    listingOffersState.mlRanking,
    queryString,
    offersCount,
    mlSearchSessionGuid,
    deviceType,
  ]);

  const onFullscreenToggle = React.useCallback(() => {
    dispatch(toggleModal({ open: true, mode: EListingModalMode.FULL_SCREEN }));
  }, [dispatch]);

  React.useEffect(() => {
    anchorRef.current = document.getElementById(anchorDomId);
  }, [anchorDomId]);

  const onHalfModeToggleToFullMode = React.useCallback(() => {
    if (isPending) {
      return;
    }

    trackListingShow('all_objects_on_map')({
      offers: listingOffersState.offers,
      mlRanking: listingOffersState.mlRanking || undefined,
      jsonQuery,
      queryString,
      offersCount,
      mlSearchSessionGuid,
    });

    onFullscreenToggle();
  }, [
    jsonQuery,
    listingOffersState.mlRanking,
    listingOffersState.offers,
    onFullscreenToggle,
    queryString,
    offersCount,
    isPending,
    mlSearchSessionGuid,
  ]);

  const listingButtonClickHandler = onOpen;

  return (
    <>
      <div className={styles['toggle']}>
        <Button
          onClick={listingButtonClickHandler}
          theme="fill_white_primary"
          size="M"
          beforeIcon={<IconList16 color="black_100" />}
          data-testid={ETestId.ListingButton}
        >
          Список
        </Button>
      </div>
      <PortalWrapper>
        <SwipeModal
          className={styles['modal']}
          maxHeight={isHalfScreenMode ? maxHeight / 2 : maxHeight}
          fixHeight={true}
          show={show}
          header={!isPending && hasListingOffers && <ModalHeader title={seoTitle} sortSlot={<SortContainer />} />}
          halfScreen={isHalfScreenMode}
          onClose={onClose}
          onCloseButtonClick={onCloseButtonClick}
          onFullscreenToggle={onFullscreenToggle}
          hasCloseButton={!isPending}
          testId={ETestId.ListingMobile}
          onSwipeUp={isHalfScreenMode ? onHalfModeToggleToFullMode : undefined}
          onSwipeDown={onSwipeClose}
          shouldListenSwipeDown={shouldListenSwipeDown}
        >
          <ListingMobileContainer />
        </SwipeModal>
      </PortalWrapper>

      <ChatModalContainer />
    </>
  );
};
